'use client';

import React, { useState, useEffect, useRef, Ref } from 'react';
import classNames from 'classnames';
import { ConIconAtom } from '@gandalf/babylon/atoms/icon';
import ChevronDownThinIcon from '@gandalf/babylon/dist/icons/chevron-down-thin.svg';
import cls from './Accordion.module.scss';

type AccordionType = {
  title: React.ReactNode;
  content: React.ReactNode;
  maxHeight?: number;
  isExpanded?: boolean;
  className?: string;
  contentRef?: Ref<HTMLDivElement>;
  onToggle?: (isExpanded: boolean) => void;
  shouldAnimateHeader?: boolean;
  iconSize?: '11' | '13' | '16' | '20' | '24' | '26' | '48';
};

export const Accordion = ({
  title,
  maxHeight = 250,
  content,
  isExpanded = false,
  className,
  contentRef,
  onToggle,
  shouldAnimateHeader = false,
  iconSize = '20',
}: AccordionType) => {
  const [isOpen, setIsOpen] = useState(isExpanded);
  const [height, setHeight] = useState(maxHeight);
  const [isAnimating, setIsAnimating] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsOpen(isExpanded);
  }, [isExpanded]);

  const onAccordionClick = () => {
    const nextState = !isOpen;

    setIsOpen(nextState);
    onToggle?.(nextState);
  };

  useEffect(() => {
    setHeight(maxHeight);
  }, [maxHeight]);

  useEffect(() => {
    if (shouldAnimateHeader && headerRef.current) {
      const headerWidth = headerRef.current.offsetWidth;
      const offset = -(headerWidth / 2 - Number(iconSize) / 2);
      headerRef.current.style.setProperty(
        '--header-anim-offset',
        `${offset}px`,
      );
      setIsAnimating(true);
    }
  }, [iconSize, shouldAnimateHeader]);

  const handleAnimationEnd = () => {
    setIsAnimating(false);
  };

  return (
    <>
      <div
        ref={headerRef}
        className={classNames(cls.header, className)}
        onClick={onAccordionClick}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onAccordionClick();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
      >
        <span
          className={classNames(cls.titleWrapper, {
            [cls.titleAnimating]: isAnimating,
          })}
        >
          {title}
        </span>
        <span
          className={classNames({ [cls.chevronAnimating]: isAnimating })}
          onAnimationEnd={handleAnimationEnd}
        >
          <ConIconAtom
            icon={ChevronDownThinIcon}
            size={iconSize}
            color="fill-neutral-4"
            cls={classNames(cls.chevron, {
              [cls.chevronReverse]: isOpen,
            })}
            dataQa="accordion-toggle-icon"
            aria-hidden
          />
        </span>
      </div>
      <div
        className={cls.content}
        style={{ maxHeight: isOpen ? height : 0 }}
        ref={contentRef}
      >
        {content}
      </div>
    </>
  );
};
